'use client';

/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectApiError, clearApiError } from 'src/redux/slices/apiErrorSlice';
import { PulseButton } from 'src/components/PulseUI';

const HELP_CENTER_URL = 'https://help.medscout.io';
const SUPPORT_EMAIL = 'support@medscout.io';

const ERROR_CONTENT = {
  rate_limited: {
    title: 'Too Many Requests',
    description:
      'Your account has sent too many requests. The page will automatically reload shortly.',
  },
  account_blocked: {
    title: 'Account Created Successfully',
    description:
      "Your account has been created, but it doesn't have access to the MedScout web application. This is an MCP trial account.",
  },
} as const;

const SupportFooter = () => (
  <div className="flex flex-col gap-2 text-sm text-neutral-500">
    <p>
      Need help?{' '}
      <a
        href={HELP_CENTER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sky-600 hover:underline"
        aria-label="Visit our Help Center (opens in new tab)"
      >
        Visit our Help Center
      </a>
    </p>
    <p>
      Or reach out at{' '}
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="text-sky-600 hover:underline"
      >
        {SUPPORT_EMAIL}
      </a>
    </p>
  </div>
);

const AccountBlockedOverlay = ({
  message,
}: {
  message: string | null;
}) => (
  <div
    className="flex h-screen items-center justify-center bg-neutral-50 px-4"
    data-testid="api-error-overlay"
    role="alert"
  >
    <div className="flex max-w-md flex-col items-center gap-8 text-center">
      <img
        src="/medscoutLogin.png"
        alt="MedScout"
        className="h-16 w-auto"
      />
      <div className="flex flex-col gap-3">
        <h1 className="text-2xl font-bold text-secondary">
          {ERROR_CONTENT.account_blocked.title}
        </h1>
        <p className="leading-relaxed text-neutral-500">
          {message ?? ERROR_CONTENT.account_blocked.description}
        </p>
      </div>
      <div className="rounded-xl bg-sky-50 px-6 py-4">
        <p className="text-sm text-sky-700">
          Your MCP trial is ready to go — connect via your favorite AI
          assistant to start exploring MedScout data.
        </p>
      </div>
      <SupportFooter />
    </div>
  </div>
);

const RateLimitedToast = ({
  message,
  secondsLeft,
  onDismiss,
}: {
  message: string | null;
  secondsLeft: number | null;
  onDismiss: () => void;
}) => (
  <div
    className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right"
    data-testid="api-error-toast"
    role="alert"
    aria-live="polite"
    aria-atomic="true"
  >
    <div className="flex items-center gap-4 rounded-xl border border-amber-300 bg-amber-50 px-5 py-4 shadow-lg">
      <div className="flex flex-col gap-1">
        <p className="font-semibold text-amber-900">
          {ERROR_CONTENT.rate_limited.title}
        </p>
        <p className="text-sm text-amber-700">
          {message ?? 'Too many requests. Retrying shortly.'}
        </p>
      </div>
      {secondsLeft != null && secondsLeft > 0 ? (
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-200 text-sm font-bold text-amber-900"
          data-testid="api-error-countdown"
        >
          {secondsLeft}
        </span>
      ) : (
        <PulseButton
          variant="plain"
          size="sm"
          onClick={onDismiss}
          className="shrink-0 text-amber-700"
          data-testid="api-error-toast-dismiss"
          aria-label="Dismiss"
        >
          Dismiss
        </PulseButton>
      )}
    </div>
  </div>
);

const ApiErrorOverlay = () => {
  const { reason, message, retryAfterMs } = useSelector(selectApiError);
  const dispatch = useDispatch();
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // Initialize countdown when retryAfterMs is set
  useEffect(() => {
    if (reason !== 'rate_limited' || retryAfterMs == null) {
      setSecondsLeft(null);
      return;
    }

    // Retry-After already elapsed or zero — reload immediately
    if (retryAfterMs <= 0) {
      dispatch(clearApiError());
      window.location.reload();
      return;
    }

    const total = Math.ceil(retryAfterMs / 1000);
    setSecondsLeft(total);

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev == null || prev <= 1) {
          clearInterval(interval);
          dispatch(clearApiError());
          window.location.reload();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [reason, retryAfterMs, dispatch]);

  if (!reason) return null;

  // Account blocked gets the branded MedScout / MCP trial full-page look
  if (reason === 'account_blocked') {
    return <AccountBlockedOverlay message={message} />;
  }

  // Rate limited shows a non-blocking toast with countdown
  return (
    <RateLimitedToast
      message={message}
      secondsLeft={secondsLeft}
      onDismiss={() => dispatch(clearApiError())}
    />
  );
};

export default ApiErrorOverlay;
